import { Form, FormInstance } from 'antd';
import { useEffect, useRef, useState } from 'react';

interface UseFormSubmittableOptions {
  fieldNames?: string[];
  enabled?: boolean;
  initialSubmittable?: boolean;
}

interface FormValidationError {
  outOfDate?: boolean;
}

function isEmptyValue(value: unknown) {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

export default function useFormSubmittable(
  form: FormInstance,
  options?: UseFormSubmittableOptions,
): boolean {
  const fieldNames = options?.fieldNames;
  const enabled = options?.enabled ?? true;
  const initialSubmittable = options?.initialSubmittable;
  const [submittable, setSubmittable] = useState<boolean>(() => {
    if (!enabled) {
      return false;
    }

    if (fieldNames && fieldNames.length === 0) {
      return true;
    }

    return initialSubmittable ?? true;
  });
  const formValues = Form.useWatch(null, form);
  const formFieldKeys = fieldNames
    ? fieldNames.join(',')
    : Object.keys(form.getFieldsValue()).join(','); // Forcer une revalidation lorsque des champs ont été ajoutés ou bien supprimés du formulaire
  const latestValidationRun = useRef(0);

  useEffect(() => {
    const initialValue = initialSubmittable ?? true;

    if (!enabled) {
      setSubmittable(false);
      return;
    }

    if (fieldNames && fieldNames.length === 0) {
      setSubmittable(initialValue);
      return;
    }

    const currentValidationRun = latestValidationRun.current + 1;
    latestValidationRun.current = currentValidationRun;

    if (
      fieldNames?.length &&
      fieldNames.some((fieldName) =>
        isEmptyValue(form.getFieldValue(fieldName)),
      )
    ) {
      setSubmittable(false);
      return;
    }

    const setResult = (newSubmittable: boolean) => () => {
      if (latestValidationRun.current === currentValidationRun) {
        setSubmittable(newSubmittable);
      }
    };

    const validationPromise = fieldNames?.length
      ? form.validateFields(fieldNames, { validateOnly: true })
      : form.validateFields({ validateOnly: true });

    validationPromise
      .then(setResult(true))
      .catch((error: FormValidationError) => {
        // AntD may reject with outOfDate=true when a newer validation starts.
        // Ignore that stale result and wait for the latest run.
        if (error?.outOfDate) {
          return;
        }

        setResult(false)();
      });
  }, [
    enabled,
    fieldNames,
    form,
    formValues,
    formFieldKeys,
    initialSubmittable,
  ]);

  return submittable;
}
